import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Ban,
  Bot,
  CheckIcon,
  ChevronDownIcon,
  LoaderIcon,
  XIcon,
} from 'lucide-react';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useAgentDisplayNameResolver } from '@cotera/client/app/hooks/use-agents-display-names';
import {
  ModelScopeFactory,
  useInScopeModel,
  useWatchableValue,
  Watchable,
  ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { SubAgentChatModel } from './sub-agent-chat.model';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import { HijackerQueueModel } from '@cotera/client/v0/chat/input-hijackers';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ChatModel } from '@cotera/client/v0/chat/chat.model';
import { PresentationalChat } from '@cotera/client/v0/chat/presentational-chat';
import type { MessageClass, ToolPart } from '@cotera/sdk/core';
import type {
  CustomToolRenderer,
  ToolRendererProps,
} from '@cotera/client/v0/chat/tool-renderers';
import { formatCredits } from '@cotera/client/v0/components/topbar/credit-balance-section';
import { translateToolName } from './translate-tool-name';
import { getV1ToolRenderer } from './v1-tool-renderers';
import type { ChatStatus } from '../chat/models/coco-chat.model';
import { COCO_THINKING_MESSAGE_FOOTER_ID } from '../chat/components/coco-thinking-placeholder';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';
import { Assert } from '@cotera/utilities';
import { extractSubAgentChatId } from './sub-agent-tool.model';
import { lastToolPartFailed } from './tool-part-failed';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';

const SUB_AGENT_EXCLUDED_MESSAGE_FOOTERS: readonly string[] = [
  COCO_THINKING_MESSAGE_FOOTER_ID,
];
/**
 * Marks that a `SubAgentChatPanel` is rendered inside another scroll owner, so
 * it should render at full height instead of creating its own `maxHeight` +
 * `overflow-y-auto` scroller (which would nest scrollers and trap the wheel).
 *
 * Default `false`: a standalone panel owns its own scroll. `PresentationalChat`
 * sets it `true` for nested sub-agents, and the for-each renderer sets it `true`
 * so its whole item list scrolls as a single region.
 */
export const SubAgentScrollContext = createContext(false);

function lowercaseFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

const FADE_MS = 250;

/**
 * Fades out old text, swaps, fades in new text.
 */
const FadeText: React.FC<{ text: string; className?: string }> = ({
  text,
  className,
}) => {
  const [displayed, setDisplayed] = useState(text);
  const [opacity, setOpacity] = useState(1);
  const pendingText = useRef(text);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (text === displayed) {
      return;
    }
    pendingText.current = text;
    // Fade out
    setOpacity(0);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setDisplayed(pendingText.current);
      // Small gap so browser paints opacity:0 with new text before fading in
      requestAnimationFrame(() => {
        setOpacity(1);
      });
    }, FADE_MS);
    return () => clearTimeout(timerRef.current);
  }, [text, displayed]);

  return (
    <span
      className={className}
      style={{
        opacity,
        transition: `opacity ${FADE_MS}ms ease-in-out`,
      }}
    >
      {displayed}
    </span>
  );
};

/**
 * Create a readonly chat model compatible with PresentationalChat
 */
function createReadonlyChatModel(
  conversationId: string,
  statusWatchable: ReadonlyWatchable<ChatStatus>,
  inputWatchable: Watchable<string>
): Pick<
  ChatModel,
  | 'lastMessageId'
  | 'status'
  | 'addToolResult'
  | 'id'
  | 'model'
  | 'inputWatchable'
> {
  return {
    id: conversationId,
    get lastMessageId() {
      return undefined;
    },
    get status() {
      const status = statusWatchable.snapshot();
      return status as 'ready' | 'streaming' | 'submitted' | 'error';
    },
    get model() {
      return '';
    },
    inputWatchable,
    addToolResult(_toolCallId: string, _result: unknown) {
      // noop - readonly
    },
  };
}

/**
 * Collect tool parts from messages for displaying in tool list
 */
export function collectToolParts(messages: MessageClass[]): ToolPart[] {
  const parts: ToolPart[] = [];
  for (const msg of messages) {
    if (msg.isAssistantMessage()) {
      parts.push(...msg.getToolParts());
    }
  }
  return parts;
}

/**
 * Get the last assistant text content from messages
 */
export function getLastAssistantText(messages: MessageClass[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg === undefined || !msg.isAssistantMessage()) {
      continue;
    }
    const textParts = msg.parts.filter((p) => p.isTextPart());
    if (textParts.length > 0) {
      const text = textParts
        .map((p) => ('text' in p ? String((p as { text: string }).text) : ''))
        .join('');
      if (text.trim().length > 0) {
        return text;
      }
    }
  }
  return null;
}

type SubAgentChatContentProps = {
  part: ToolPart;
  readonly?: boolean;
  maxHeight?: string;
};

export type SubAgentChatPanelProps = {
  /** Model scope id (`part.toolCallId` for standalone sub-agents, conversation id for for-each rows). */
  modelScopeKey: string;
  /** Tool key used to resolve the default display name (e.g. inner agent key for for-each). */
  toolKeyForDisplay: string;
  /** When set, replaces resolved agent name in status copy (e.g. for-each item label). */
  displayNameOverride?: string;
  /** True while the tool call that spawned this sub-agent is still incomplete. */
  triggeringToolIncomplete?: boolean;
  /** True when the tool call that spawned this sub-agent was denied or cancelled (output-denied state). */
  triggeringToolCancelled?: boolean;
  readonly?: boolean;
  maxHeight?: string;
  marginBottom?: boolean;
};

/**
 * Collapsible readonly sub-agent chat (PresentationalChat). Shared by standalone
 * sub-agent tool calls and for-each child conversations.
 */
export const SubAgentChatPanel: React.FC<SubAgentChatPanelProps> = ({
  modelScopeKey,
  toolKeyForDisplay,
  displayNameOverride,
  triggeringToolIncomplete = false,
  triggeringToolCancelled = false,
  readonly = true,
  maxHeight = '400px',
  marginBottom = true,
}) => {
  const isNestedSubAgentPanel = useContext(SubAgentScrollContext);
  const subAgentModel = useInScopeModel(SubAgentChatModel, modelScopeKey);

  Assert.assert(subAgentModel !== null);

  const conversationId = subAgentModel.conversationId;
  const status = useWatchableValue(subAgentModel.statusWatchable);
  const frameStatus = useWatchableValue(
    subAgentModel.stackFrameStatusInfoWatchable
  );
  const messages = useWatchableValue(subAgentModel.messagesWatchable);
  const totalCredits = useWatchableValue(subAgentModel.creditsWatchable);
  const hijackerQueueModel = useInScopeModel(HijackerQueueModel);

  useTracking(
    CocoEvent.SubAgentOpened,
    { conversationId, toolKey: toolKeyForDisplay },
    { enabled: conversationId !== null }
  );

  useEffect(() => {
    if (hijackerQueueModel === null) {
      return;
    }

    hijackerQueueModel.addSubAgentMessages(messages);
    return () => {
      hijackerQueueModel.removeSubAgentMessages(messages);
    };
  }, [hijackerQueueModel, messages]);

  const resolveAgentName = useAgentDisplayNameResolver();

  const displayName = useMemo(() => {
    if (displayNameOverride !== undefined && displayNameOverride !== '') {
      return displayNameOverride;
    }
    const toolKey = toolKeyForDisplay;
    return toolKey === 'coco/sub-agent'
      ? 'Sub Agent'
      : toolKey === 'coco/cotera-agent'
      ? 'Cotera Agent'
      : toolKey === 'coco/cotera-workspace'
      ? 'Cotera workspace'
      : toolKey === 'coco/agent-editor'
      ? 'Agent Editor'
      : toolKey === 'coco/document-editor'
      ? 'Document Editor'
      : toolKey === 'coco/folder-editor'
      ? 'Folder editor'
      : resolveAgentName(toolKey) ?? 'Sub Agent';
  }, [displayNameOverride, toolKeyForDisplay, resolveAgentName]);

  // If the initiating tool call is already cancelled/denied, don't show a spinner
  // even while the status API is still propagating.
  const isRunning =
    (status === 'streaming' || status === 'submitted') &&
    !triggeringToolCancelled;
  const isCancelled =
    frameStatus.statusKey === 'cancelled' || triggeringToolCancelled;

  const toolParts = useMemo(() => collectToolParts(messages), [messages]);
  const toolNeedingApproval = useMemo(
    () => toolParts.find((tp) => tp.approvalStatus() === 'required') ?? null,
    [toolParts]
  );
  const anyToolNeedsApproval = toolNeedingApproval !== null;
  const anyToolWasDenied = useMemo(
    () => toolParts.some((tp) => tp.approvalStatus() === 'denied'),
    [toolParts]
  );
  const lastToolFailed = useMemo(
    () => lastToolPartFailed(toolParts),
    [toolParts]
  );
  // The sub-agent's turn can end while a workflow it triggered is still
  // running (the tool call stays incomplete until the run finishes and resumes
  // the sub-agent), so the frame alone reads as done.
  const isAwaitingWorkflow = useMemo(
    () =>
      toolParts.some((tp) => tp.isWorkflowToolCall() && tp.phase === 'running'),
    [toolParts]
  );
  const lastAssistantText = useMemo(
    () => getLastAssistantText(messages),
    [messages]
  );
  const hasCompletedResponse =
    lastAssistantText !== null || frameStatus.isComplete || status === 'ready';

  const hasSeenTools = useRef(false);
  const cachedToolParts = useRef(toolParts);
  if (toolParts.length > 0) {
    hasSeenTools.current = true;
    cachedToolParts.current = toolParts;
  }

  const isAwaitingTriggerCompletion =
    !isRunning &&
    triggeringToolIncomplete &&
    !isCancelled &&
    !anyToolWasDenied &&
    !lastToolFailed;
  const isWaitingOnWorkflow =
    !isRunning && isAwaitingWorkflow && !isCancelled && status !== 'error';
  const isActive =
    isRunning || isAwaitingTriggerCompletion || isWaitingOnWorkflow;

  const narrative = useMemo(() => {
    if (anyToolNeedsApproval) {
      return `${displayName} needs approval`;
    }
    if (!isRunning && isCancelled) {
      return `${displayName} was cancelled`;
    }
    if (!isRunning && anyToolWasDenied) {
      return `${displayName} was stopped`;
    }
    if (!isRunning && lastToolFailed) {
      return `${displayName} ran into an issue`;
    }
    if (isWaitingOnWorkflow) {
      return `${displayName} is waiting on a workflow…`;
    }
    if (
      !isRunning &&
      (toolParts.length > 0 || hasCompletedResponse) &&
      (!triggeringToolIncomplete || hasCompletedResponse)
    ) {
      return `${displayName} finished`;
    }
    // Find the last tool that's still running (no output yet)
    for (let i = toolParts.length - 1; i >= 0; i--) {
      const tp = toolParts[i]!;
      if (!tp.hasOutput || tp.isIncomplete()) {
        const action = translateToolName(tp.toolKey().toString(), 'running');
        return `${displayName} is ${lowercaseFirst(action)}`;
      }
    }
    if (hasSeenTools.current) {
      return `${displayName} is thinking…`;
    }
    return `Waking up ${displayName}…`;
  }, [
    isRunning,
    isCancelled,
    toolParts,
    displayName,
    anyToolNeedsApproval,
    anyToolWasDenied,
    lastToolFailed,
    isWaitingOnWorkflow,
    triggeringToolIncomplete,
    hasCompletedResponse,
  ]);

  const [isOpen, setIsOpen] = useState(false);

  // Create readonly chat model for PresentationalChat
  const inputWatchable = useMemo(() => Watchable.fromValue(''), []);
  const chatModel = useMemo(
    () =>
      createReadonlyChatModel(
        conversationId ?? '',
        subAgentModel.statusWatchable,
        inputWatchable
      ),
    [conversationId, subAgentModel.statusWatchable, inputWatchable]
  );

  // Custom tool renderer that delegates to v1 renderers for nested sub-agents
  const customToolRenderers = useMemo(() => {
    return (toolKey: string) => {
      return getV1ToolRenderer(toolKey);
    };
  }, []);

  if (conversationId === null) {
    // Only spin while the triggering tool is still initializing. Once it has
    // completed or been cancelled without a conversation ID, show a static
    // terminal state so the spinner doesn't persist forever.
    if (!triggeringToolIncomplete || triggeringToolCancelled) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
          {triggeringToolCancelled ? (
            <Ban className="h-4 w-4 shrink-0 text-yellow-700 dark:text-yellow-300" />
          ) : (
            <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />
          )}
          <span className="italic text-sm">
            {triggeringToolCancelled
              ? `${displayName} was cancelled`
              : `${displayName} finished`}
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
        <LoaderIcon className="h-4 w-4 animate-spin" />
        <RunningToolTitle className="italic text-sm">
          Waking up {displayName}…
        </RunningToolTitle>
      </div>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn('group not-prose w-full', marginBottom ? 'mb-4' : '')}
    >
      <div className="flex items-center gap-2 py-1">
        <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-2 text-left min-w-0">
          <div
            className={cn(
              'flex items-center gap-2 text-sm italic min-w-0',
              isCancelled
                ? 'text-yellow-900 dark:text-yellow-100'
                : 'text-muted-foreground'
            )}
          >
            <Bot className="h-4 w-4 shrink-0" />
            {isActive ? (
              <LoaderIcon className="h-4 w-4 shrink-0 animate-spin" />
            ) : isCancelled ? (
              <Ban className="h-4 w-4 shrink-0 text-yellow-700 dark:text-yellow-300" />
            ) : anyToolWasDenied || lastToolFailed ? (
              <XIcon className="h-4 w-4 shrink-0 text-orange-600" />
            ) : (
              <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />
            )}
            {isActive ? (
              <RunningToolTitle className="italic text-sm min-w-0">
                <FadeText text={narrative} />
              </RunningToolTitle>
            ) : (
              <FadeText text={narrative} className="italic" />
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground/70 font-normal">
            {totalCredits > 0 && (
              <>
                <Icon icon="credits" />
                <span className="not-italic whitespace-nowrap shrink-0">
                  {isActive ? '~' : ''}
                  {formatCredits(totalCredits)} credits
                </span>
              </>
            )}
            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </div>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent
        className={cn(
          'min-h-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2',
          'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:slide-in-from-top-2'
        )}
      >
        <div className="mt-2 overflow-hidden rounded-md border border-border bg-muted/30">
          {/* Single scroll region: PresentationalChat uses plain layout (no StickToBottom) so trackpad/wheel is not lost to nested scroll + use-stick-to-bottom. */}
          <div
            className={cn(
              'px-2 py-2 [&_*]:text-sm [&_.message-gap]:space-y-2 [&_.avatar]:h-5 [&_.avatar]:w-5 [&_.message-padding]:py-1 [&_.message-padding]:px-2',
              !isNestedSubAgentPanel && 'overflow-y-auto overscroll-y-contain'
            )}
            style={
              !isNestedSubAgentPanel
                ? {
                    maxHeight,
                  }
                : undefined
            }
            onWheel={
              !isNestedSubAgentPanel
                ? (e) => {
                    e.stopPropagation();
                  }
                : undefined
            }
          >
            <SubAgentScrollContext.Provider value={true}>
              <PresentationalChat
                useNativeConversationScroll
                excludedMessageFooterIds={SUB_AGENT_EXCLUDED_MESSAGE_FOOTERS}
                status={
                  status as
                    | 'ready'
                    | 'streaming'
                    | 'submitted'
                    | 'error'
                    | 'loading'
                }
                messages={messages}
                onSubmit={() => {
                  /* readonly - no submit */
                }}
                readonly={readonly}
                hideInput={true}
                suppressHijackers={true}
                chatModel={chatModel}
                customToolRenderers={customToolRenderers}
                conversationId={conversationId}
                messageActionsConfig={{ showReplay: false, showEdit: false }}
              />
            </SubAgentScrollContext.Provider>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const SubAgentChatContent: React.FC<SubAgentChatContentProps> = ({
  part,
  readonly,
  maxHeight,
}) => {
  const triggeringToolCancelled = part.isDenied();

  return (
    <SubAgentChatPanel
      modelScopeKey={part.toolCallId}
      toolKeyForDisplay={part.toolKey().toString()}
      triggeringToolIncomplete={part.isIncomplete()}
      triggeringToolCancelled={triggeringToolCancelled}
      readonly={readonly}
      maxHeight={maxHeight}
    />
  );
};

/**
 * Outer wrapper component that creates the model scope
 */
const SubAgentChatRendererOuter: React.FC<{
  part: ToolRendererProps['part'];
  readonly?: boolean;
  maxHeight?: string;
}> = ({ part, readonly, maxHeight }) => {
  const client = useTenantedClient();
  const watchablesFactory = useWatchablesFactory();
  const eventConfig = useMemo(
    () => ({ client: client.resource.chat }),
    [client.resource.chat]
  );
  const conversationId = extractSubAgentChatId(part);

  const createModels = useCallback(
    () => [
      new SubAgentChatModel({
        part,
        eventConfig,
        conversationIdOverride: conversationId ?? undefined,
        modelId: part.toolCallId,
        watchablesFactory,
      }),
    ],
    [part, eventConfig, conversationId, watchablesFactory]
  );

  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[
        part.toolCallId,
        conversationId ?? '',
        eventConfig,
        watchablesFactory,
      ]}
    >
      <SubAgentChatContent
        part={part}
        readonly={readonly}
        maxHeight={maxHeight}
      />
    </ModelScopeFactory>
  );
};

/**
 * Create a SubAgentChatRenderer (v2).
 * This is a collapsible readonly chat view that propagates hijacks up to parent contexts.
 */
export function createSubAgentChatRenderer(): CustomToolRenderer {
  return (props) => (
    <SubAgentChatRendererOuter part={props.part} readonly={props.readonly} />
  );
}

export default SubAgentChatRendererOuter;
